/**
 * Bill view helpers (BILLING-MISSING-DELTA-V1).
 *
 * Pure, react-free logic for the itemized APPOINTMENT BILL surfaces: wire
 * mirrors of api/routes/patient-bills.ts, display labels, and the input
 * parsing that rides the EXISTING money grammar.
 *
 * REUSE-BEFORE-BUILD (binding):
 *   - MONEY TRUTH is the server's. This module DISPLAYS the bill wire's own
 *     derived totals (gross / waived / gst / net from THE ONE derivation,
 *     lib/patient-bills.ts deriveBillTotals) and performs NO netting. The
 *     only arithmetic here is a per-line display multiply of two wire
 *     integers (quantity x unit price) for the line-amount cell -- the same
 *     numbers the server summed; nothing is re-derived.
 *   - THE FORMATTER is the existing formatMinorUnitsInr (billing-entry-view
 *     .ts). No new money formatter, no changed grammar.
 *   - THE PARSER is the existing parseInrToMinorUnits (string math). A
 *     second parser is forbidden.
 *
 * VOCABULARY: "Gross amount" / "Net amount" are BILL-DOCUMENT facts (the
 * tracker's own words). This module never says "Amount due", "Balance",
 * "Outstanding balance", "Fully paid", or "invoice" -- the account grain
 * does not exist (gap.patient-account-model) and the per-episode
 * outstanding keeps its own home + vocabulary (billing queue).
 */

import { parseInrToMinorUnits } from '@/dashboard/billing/billing-entry-view';
import { PortalApiError } from '@/lib/portal-api';

// ---- Wire mirrors (api/routes/patient-bills.ts) ----------------------------

export interface PatientBillItemWireMirror {
  readonly id: number;
  readonly treatmentName: string;
  readonly performingDoctorId: number | null;
  readonly performingDoctorName: string | null;
  readonly planItemId: number | null;
  readonly unitPriceMinorUnits: number | null;
  readonly quantity: number;
  readonly waivedOff: boolean;
}

export interface PatientBillTotalsMirror {
  readonly grossMinorUnits: number;
  readonly waivedMinorUnits: number;
  readonly gstMinorUnits: number | null;
  readonly netMinorUnits: number;
  readonly unpricedCount: number;
}

export interface PatientBillWireMirror {
  readonly consultationPlanSnapshotPending?: boolean | null;
  readonly id: number;
  readonly clinicId: number;
  readonly patientId: string;
  readonly patientName: string | null;
  readonly appointmentId: number;
  readonly status: 'draft' | 'finalized' | 'cancelled';
  readonly gstMinorUnits: number | null;
  readonly finalizedAt: string | null;
  readonly cancelledAt: string | null;
  readonly cancelReason: string | null;
  readonly createdAt: string;
  readonly items: readonly PatientBillItemWireMirror[];
  readonly balance?: {readonly state:'known'|'allocation_unknown'|'not_finalized'|'unavailable';readonly paidMinorUnits:number|null;readonly dueMinorUnits:number|null};
  readonly totals: PatientBillTotalsMirror;
}

export interface PatientBillResponseMirror {
  readonly bill: PatientBillWireMirror;
  readonly canCancelFinalized: boolean;
}

export interface PatientBillListResponseMirror {
  readonly scope?: { readonly patientId: string | null; readonly patientFamilyIds: readonly string[] | null; readonly clinicId: number | null; readonly appointmentId: number | null; readonly billId: number | null };
  readonly bills: readonly PatientBillWireMirror[];
  readonly canCancelFinalized: boolean;
  /** B1: the window this page answers, and whether OLDER bills exist beyond
   *  it. OPTIONAL so a pre-corrective payload keeps rendering (an absent
   *  hasMore reads as "no more", which is what the old wire meant). */
  readonly offset?: number;
  readonly limit?: number;
  readonly hasMore?: boolean;
}

export interface CreatePatientBillResponseMirror
  extends PatientBillResponseMirror {
  readonly created: boolean;
  readonly seededFromPlanCount: number;
}

export interface BillDoctorOption {
  readonly doctorId: number;
  readonly doctorName: string;
}

// ---- THE TOLERANT WIRE BOUNDARY (walk24-2 corrective) ----------------------
//
// LES-057 posture, proven again by CI on this very package: a serving side
// that does not carry the bill contract -- the MOCK harness's generic `{}`
// default, a stale deploy without the bill routes, any malformed answer --
// must degrade to the VISIBLY HONEST store-not-available state, never crash
// the page render and never leave a silently blank panel. An array method is
// NEVER called on a wire field before this boundary proves it is an array.
// ONE boundary, every consumer (front-desk panel, COC lane): both read the
// wire exclusively through readPatientBillListWire below.

/** What a bill-list read resolves to AFTER the tolerant boundary. */
export type PatientBillListReadState =
  | {
      readonly kind: 'ready';
      readonly bills: readonly PatientBillWireMirror[];
      readonly canCancelFinalized: boolean;
    }
  /** The serving side does not carry the bill contract (pre-apply backend,
   *  stale server, mock default, malformed answer) -- render the honest
   *  store-not-available line, never a dead door, never a crash. */
  | { readonly kind: 'not_served' };

const BILL_STATUSES = new Set(['draft', 'finalized', 'cancelled']);

function isSafeInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isSafeInteger(v);
}

function isIntOrNull(v: unknown): v is number | null {
  return v === null || isSafeInt(v);
}

function readBillItem(raw: unknown): PatientBillItemWireMirror | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isSafeInt(r.id)) return null;
  if (typeof r.treatmentName !== 'string') return null;
  if (!isSafeInt(r.quantity) || r.quantity <= 0) return null;
  if (!isIntOrNull(r.unitPriceMinorUnits ?? null)) return null;
  return {
    id: r.id,
    treatmentName: r.treatmentName,
    performingDoctorId: isSafeInt(r.performingDoctorId) ? r.performingDoctorId : null,
    performingDoctorName:
      typeof r.performingDoctorName === 'string' ? r.performingDoctorName : null,
    planItemId: isSafeInt(r.planItemId) ? r.planItemId : null,
    unitPriceMinorUnits: isSafeInt(r.unitPriceMinorUnits)
      ? r.unitPriceMinorUnits
      : null,
    quantity: r.quantity,
    waivedOff: r.waivedOff === true,
  };
}

function readBillTotals(raw: unknown): PatientBillTotalsMirror | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (
    !isSafeInt(r.grossMinorUnits) ||
    !isSafeInt(r.waivedMinorUnits) ||
    !isSafeInt(r.netMinorUnits) ||
    !isSafeInt(r.unpricedCount) ||
    !isIntOrNull(r.gstMinorUnits ?? null)
  ) {
    return null;
  }
  return {
    grossMinorUnits: r.grossMinorUnits,
    waivedMinorUnits: r.waivedMinorUnits,
    gstMinorUnits: isSafeInt(r.gstMinorUnits) ? r.gstMinorUnits : null,
    netMinorUnits: r.netMinorUnits,
    unpricedCount: r.unpricedCount,
  };
}

function readBill(raw: unknown): PatientBillWireMirror | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isSafeInt(r.id)) return null;
  if (!isSafeInt(r.clinicId)) return null;
  if (typeof r.patientId !== 'string') return null;
  if (!isSafeInt(r.appointmentId)) return null;
  if (typeof r.status !== 'string' || !BILL_STATUSES.has(r.status)) return null;
  if (!Array.isArray(r.items)) return null;
  const items: PatientBillItemWireMirror[] = [];
  for (const rawItem of r.items) {
    const item = readBillItem(rawItem);
    if (item === null) return null;
    items.push(item);
  }
  const totals = readBillTotals(r.totals);
  if (totals === null) return null;
  const rawBalance=r.balance as {state?:unknown;paidMinorUnits?:unknown;dueMinorUnits?:unknown}|undefined;
  const balance:NonNullable<PatientBillWireMirror['balance']>=rawBalance?.state==='known'&&isSafeInt(rawBalance.paidMinorUnits)&&isSafeInt(rawBalance.dueMinorUnits)&&rawBalance.paidMinorUnits>=0&&rawBalance.dueMinorUnits>=0
    ?{state:'known',paidMinorUnits:rawBalance.paidMinorUnits,dueMinorUnits:rawBalance.dueMinorUnits}
    :{state:rawBalance?.state==='allocation_unknown'?'allocation_unknown':rawBalance?.state==='not_finalized'?'not_finalized':'unavailable',paidMinorUnits:null,dueMinorUnits:null};
  return {
    consultationPlanSnapshotPending: typeof r.consultationPlanSnapshotPending === 'boolean' ? r.consultationPlanSnapshotPending : null,
    id: r.id,
    clinicId: r.clinicId,
    patientId: r.patientId,
    patientName: typeof r.patientName === 'string' ? r.patientName : null,
    appointmentId: r.appointmentId,
    status: r.status as PatientBillWireMirror['status'],
    gstMinorUnits: isSafeInt(r.gstMinorUnits) ? r.gstMinorUnits : null,
    finalizedAt: typeof r.finalizedAt === 'string' ? r.finalizedAt : null,
    cancelledAt: typeof r.cancelledAt === 'string' ? r.cancelledAt : null,
    cancelReason: typeof r.cancelReason === 'string' ? r.cancelReason : null,
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : '',
    items,
    totals,
    balance,
  };
}

/**
 * Read a bill-LIST response through the tolerant boundary. `bills` missing
 * or not an array = the wire is not served here -> 'not_served' (the honest
 * unavailable state). An EMPTY array is a REAL empty (the server carries
 * the contract; the mint door is lawful). ANY malformed bill row rejects
 * the whole answer as 'not_served' -- a half-shaped money wire is a
 * contract mismatch, never partial truth.
 */
export function readPatientBillListWire(data: unknown): PatientBillListReadState {
  if (data === null || typeof data !== 'object') return { kind: 'not_served' };
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.bills)) return { kind: 'not_served' };
  const bills: PatientBillWireMirror[] = [];
  for (const raw of d.bills) {
    const bill = readBill(raw);
    if (bill === null) return { kind: 'not_served' };
    bills.push(bill);
  }
  return {
    kind: 'ready',
    bills,
    canCancelFinalized: d.canCancelFinalized === true,
  };
}

/** Read the doctor-options wire tolerantly: not-an-array -> null (the
 *  picker degrades to the text label -- honest absence, never a crash). */
export function readBillDoctorOptionsWire(
  data: unknown,
): readonly BillDoctorOption[] | null {
  if (data === null || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.doctors)) return null;
  const out: BillDoctorOption[] = [];
  for (const raw of d.doctors) {
    if (raw === null || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    if (isSafeInt(r.doctorId) && typeof r.doctorName === 'string' && r.doctorName !== '') {
      out.push({ doctorId: r.doctorId, doctorName: r.doctorName });
    }
  }
  return out;
}

/**
 * TRUE for the pre-apply answer the REAL backend serves (the honest 409
 * bill_store_not_available). Branches on the PortalApiError CODE first
 * (the portal-api contract: never substring-match when a code exists);
 * the message fallback keeps non-portal error shapes honest.
 */
export function isBillStoreUnavailableError(err: unknown): boolean {
  if (err instanceof PortalApiError && err.code === 'bill_store_not_available') {
    return true;
  }
  return /bill_store_not_available|itemized-bill store is not available/i.test(
    err instanceof Error ? err.message : String(err),
  );
}

// ---- Labels ---------------------------------------------------------------

/** The ruled unset-price copy (W-B3 founder order 2026-08-01 vocabulary --
 *  reused verbatim, never "Rs 0"). */
export const BILL_PRICE_NOT_SET_COPY = 'Price not set';

/** The unassigned-doctor honesty copy (the doctor-os F6 vocabulary). */
export const BILL_DOCTOR_UNASSIGNED_COPY = 'Unassigned';

export const BILL_STATUS_LABELS: Readonly<
  Record<PatientBillWireMirror['status'], string>
> = {
  draft: 'Draft',
  finalized: 'Finalized',
  cancelled: 'Cancelled',
};

/** Cancel reasons reuse the payment VOID vocabulary verbatim. */
export const BILL_CANCEL_REASON_OPTIONS = [
  'entry_error',
  'duplicate',
  'other',
] as const;

/** The line's display doctor: roster name, else the honest fallback. */
export function billLineDoctorLabel(item: PatientBillItemWireMirror): string {
  if (item.performingDoctorName !== null) return item.performingDoctorName;
  if (item.performingDoctorId !== null) {
    return `Doctor #${item.performingDoctorId}`;
  }
  return BILL_DOCTOR_UNASSIGNED_COPY;
}

/** Line amount = the wire's own two integers multiplied for display
 *  (quantity x unit price); null when the price is not set. The server's
 *  totals remain the ONE summed truth -- this cell restates a single
 *  line's product, never a total. */
export function billLineAmountMinorUnits(
  item: PatientBillItemWireMirror,
): number | null {
  if (item.unitPriceMinorUnits === null) return null;
  return item.unitPriceMinorUnits * item.quantity;
}

// ---- Input parsing (the EXISTING string-math parser, reused) ---------------

/** Parse a rupee price input -> minor units. '' = clear ("Price not set");
 *  '0' is a REAL zero price (a genuinely free line differs from unset). */
export function parseBillPriceInput(
  raw: string,
): { ok: true; minorUnits: number | null } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, minorUnits: null };
  if (/^0(\.0{1,2})?$/.test(trimmed)) return { ok: true, minorUnits: 0 };
  const parsed = parseInrToMinorUnits(trimmed);
  if (parsed === null) return { ok: false };
  return { ok: true, minorUnits: parsed };
}

/** Parse a units input -> positive integer (default 1 for ''). */
export function parseBillQuantityInput(
  raw: string,
): { ok: true; quantity: number } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, quantity: 1 };
  if (!/^[0-9]+$/.test(trimmed)) return { ok: false };
  const n = Number(trimmed);
  if (!Number.isSafeInteger(n) || n <= 0) return { ok: false };
  return { ok: true, quantity: n };
}

/** Parse the GST amount input -> minor units. '' = clear (not recorded);
 *  '0' is a real recorded zero. */
export function parseBillGstInput(
  raw: string,
): { ok: true; minorUnits: number | null } | { ok: false } {
  return parseBillPriceInput(raw);
}

/** The price input's display string for an existing line (round-trips
 *  through parseInrToMinorUnits; '' for unset). */
export function billPriceInputValue(minorUnits: number | null): string {
  if (minorUnits === null) return '';
  return minorUnits % 100 === 0
    ? String(minorUnits / 100)
    : (minorUnits / 100).toFixed(2);
}

/** New Home destinations require a complete, matching read envelope, including empty lists. */
export function billListMatchesScope(response: PatientBillListResponseMirror, expected: {
  patientId: string | null; clinicId: number; appointmentId: number | null; billId: number | null;
  patientFamilyIds?: readonly string[];
}): boolean {
  const scope = response.scope;
  if (!scope || scope.patientId !== expected.patientId || scope.clinicId !== expected.clinicId ||
      scope.appointmentId !== expected.appointmentId || scope.billId !== expected.billId) return false;
  const family = expected.patientId === null ? expected.patientFamilyIds : scope.patientFamilyIds;
  if (!Array.isArray(family) || family.length === 0 || family.some(id => typeof id !== 'string' || !id) ||
      (expected.patientId !== null && !family.includes(expected.patientId))) return false;
  return Array.isArray(response.bills) && response.bills.every(bill => bill &&
    bill.clinicId === expected.clinicId && family.includes(bill.patientId) &&
    (expected.appointmentId === null || bill.appointmentId === expected.appointmentId) &&
    (expected.billId === null || bill.id === expected.billId));
}
